<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">{{ $t('Tax Consent') }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="6">
              <div class="text-subtitle-1 font-weight-medium">
                <v-icon class="mr-1">mdi-shield-check-outline</v-icon>
                {{ $t('Consent Versions') }}
              </div>
              <p class="text-caption text-grey mt-1">
                {{ $t('taxConsentVersionsCaption') }}
              </p>
            </v-col>

            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end ga-2">
              <v-btn
                data-testid="duplicate-active-btn"
                variant="outlined"
                color="primary"
                class="control-btn"
                size="large"
                prepend-icon="mdi-content-copy"
                rounded="lg"
                :loading="duplicating"
                @click="duplicateActive"
              >
                DUPLICATE ACTIVE
                <v-tooltip activator="parent" location="top">Duplicate the active version</v-tooltip>
              </v-btn>

              <v-btn
                data-testid="add-version-btn"
                color="primary"
                class="control-btn create-btn"
                variant="flat"
                size="large"
                prepend-icon="mdi-plus-circle"
                rounded="lg"
                @click="openAddDialog"
              >
                ADD VERSION
                <v-tooltip activator="parent" location="top">Add new consent version</v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-data-table
          data-testid="tax-consent-table"
          :headers="headers"
          :items="versions"
          :loading="loading"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          hide-default-body
        >
          <template v-slot:colgroup>
            <col
              v-for="header in headers"
              :key="header.key"
              :style="{ width: header.width, minWidth: header.width }"
            />
          </template>

          <template v-slot:tbody>
            <tbody v-if="versions.length" class="v-data-table__tbody">
              <tr
                v-for="(element, index) in versions"
                :key="element.consentVersionID || index"
                class="v-data-table__tr tax-consent-row"
              >
                <td class="v-data-table__td text-center no-column">
                  <span class="table-text">{{ index + 1 }}</span>
                </td>
                <td class="v-data-table__td version-column">
                  <div class="version-cell">
                    <span class="table-text font-weight-medium">{{ element.versionCode }}</span>
                    <v-chip
                      v-if="element.isActive"
                      color="success"
                      variant="tonal"
                      size="x-small"
                      class="ml-2"
                    >
                      Active
                    </v-chip>
                  </div>
                </td>
                <td class="v-data-table__td desc-column">
                  <span class="table-text">{{ element.description || '-' }}</span>
                </td>
                <td class="v-data-table__td date-column">
                  <span class="table-text">{{ formatDate(element.effectiveStartDate) }}</span>
                </td>
                <td class="v-data-table__td date-column">
                  <span class="table-text">{{ formatDate(element.effectiveEndDate) }}</span>
                </td>
                <td class="v-data-table__td date-column">
                  <span class="table-text">{{ formatDate(element.updateDate || element.createDate) }}</span>
                </td>
                <td class="v-data-table__td active-column">
                  <div class="d-flex justify-center">
                    <v-switch
                      :data-testid="`active-switch-${element.consentVersionID}`"
                      :model-value="element.isActive"
                      color="success"
                      density="compact"
                      hide-details
                      inset
                      @update:model-value="toggleActive(element, $event)"
                    />
                  </div>
                </td>
                <td class="v-data-table__td actions-column">
                  <div class="action-group">
                    <v-btn
                      :data-testid="`edit-btn-${element.consentVersionID}`"
                      variant="text"
                      density="comfortable"
                      icon
                      class="action-btn"
                      @click="openEdit(element)"
                    >
                      <v-icon size="18">mdi-pencil-outline</v-icon>
                      <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                    </v-btn>

                    <v-btn
                      :data-testid="`delete-btn-${element.consentVersionID}`"
                      variant="text"
                      density="comfortable"
                      icon
                      class="action-btn delete-btn"
                      @click="openDeleteDialog(element)"
                    >
                      <v-icon size="18">mdi-delete-outline</v-icon>
                      <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else class="v-data-table__tbody">
              <tr>
                <td :colspan="headers.length">
                  <div class="no-data-container">
                    <div class="no-data-content">
                      <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                        mdi-shield-off-outline
                      </v-icon>
                      <div class="no-data-text">
                        <h3 class="no-data-title">No Consent Versions Found</h3>
                        <p class="no-data-subtitle">Create your first consent version to get started.</p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-dialog v-model="addDialog" max-width="640" persistent data-testid="version-dialog">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          Add Version
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid">
            <v-text-field
              data-testid="version-code-input"
              v-model="formData.versionCode"
              label="Version Code *"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.max20]"
              prepend-inner-icon="mdi-identifier"
              placeholder="e.g. V2"
              @update:model-value="formData.versionCode = String(formData.versionCode || '').toUpperCase()"
            />

            <v-text-field
              data-testid="version-description-input"
              v-model="formData.description"
              label="Description"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text"
            />

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="version-start-input"
                  v-model="formData.effectiveStartDate"
                  label="Effective Start Date"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar-start"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  data-testid="version-end-input"
                  v-model="formData.effectiveEndDate"
                  label="Effective End Date"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar-end"
                />
              </v-col>
            </v-row>

            <v-switch
              data-testid="version-active-switch"
              v-model="formData.isActive"
              label="Active"
              color="success"
              inset
              hide-details
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="version-cancel-btn" variant="outlined" @click="closeAddDialog">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="version-save-btn"
            color="primary"
            variant="elevated"
            :disabled="!formValid"
            :loading="saving"
            @click="createVersion"
          >
            <v-icon start>mdi-content-save</v-icon>
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px" persistent data-testid="version-delete-dialog">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            This action cannot be undone.
          </v-alert>
          <p>Are you sure you want to delete the version:</p>
          <p class="font-weight-bold text-primary">
            {{ selectedVersion?.versionCode }}
            <template v-if="selectedVersion?.description">- {{ selectedVersion?.description }}</template>
          </p>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn data-testid="version-delete-cancel-btn" variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            data-testid="version-delete-confirm-btn"
            color="error"
            variant="elevated"
            :loading="deleting"
            @click="confirmDelete"
          >
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "TaxConsent",
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      loading: false,
      saving: false,
      deleting: false,
      duplicating: false,
      versions: [],
      headers: [
        { title: 'No.', key: 'no', align: 'center', sortable: false, width: '70px' },
        { title: 'Version', key: 'versionCode', align: 'start', sortable: false, width: '180px' },
        { title: 'Description', key: 'description', align: 'start', sortable: false, width: '260px' },
        { title: 'Effective Start', key: 'effectiveStartDate', align: 'start', sortable: false, width: '150px' },
        { title: 'Effective End', key: 'effectiveEndDate', align: 'start', sortable: false, width: '150px' },
        { title: 'Updated', key: 'updateDate', align: 'start', sortable: false, width: '150px' },
        { title: 'Active', key: 'isActive', align: 'center', sortable: false, width: '110px' },
        { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '140px' },
      ],
      addDialog: false,
      formValid: false,
      formData: {
        versionCode: '',
        description: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        isActive: false,
      },
      selectedVersion: null,
      deleteDialog: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: (value) => value !== null && value !== undefined && String(value).trim() !== '' || 'Required',
        max20: (value) => String(value || '').length <= 20 || 'Maximum 20 characters',
      },
    };
  },
  mounted() {
    this.loadVersions();
  },
  methods: {
    getEmptyForm() {
      return {
        versionCode: '',
        description: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        isActive: false,
      };
    },

    normalizeVersion(item) {
      return {
        consentVersionID: item.consentVersionID ?? item.ConsentVersionID,
        versionCode: item.versionCode ?? item.VersionCode ?? '',
        description: item.description ?? item.Description ?? '',
        effectiveStartDate: item.effectiveStartDate ?? item.EffectiveStartDate ?? null,
        effectiveEndDate: item.effectiveEndDate ?? item.EffectiveEndDate ?? null,
        isActive: item.isActive ?? item.IsActive ?? false,
        createDate: item.createDate ?? item.CreateDate ?? null,
        updateDate: item.updateDate ?? item.UpdateDate ?? null,
      };
    },

    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        // Value is not parseable as a date; show the raw string if present.
        return String(value).trim() || '-';
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    extractErrorMessage(error, fallback) {
      return error?.response?.data?.message || error?.response?.data?.Message || fallback;
    },

    callApi(methodName, ...args) {
      return new Promise((resolve, reject) => {
        if (!this.$call || typeof this.$call[methodName] !== 'function') {
          reject(new Error(`${methodName} is not available`));
          return;
        }

        this.$call[methodName](...args, resolve, reject);
      });
    },

    async validateForm() {
      const result = await this.$refs.form?.validate();
      return typeof result === 'object' ? result.valid : result;
    },

    async loadVersions() {
      this.loading = true;
      try {
        const response = await this.callApi('GetTaxConsentVersions');
        if (response.status === 0) {
          this.versions = (response.data || []).map(this.normalizeVersion);
        } else {
          this.showError(response.message || 'Failed to load consent versions');
        }
      } catch (error) {
        console.error('Error loading consent versions:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to load consent versions'));
      } finally {
        this.loading = false;
      }
    },

    openEdit(element) {
      this.$router.push('/TaxConsent/' + element.versionCode);
    },

    openAddDialog() {
      this.formData = this.getEmptyForm();
      this.addDialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },

    closeAddDialog() {
      this.addDialog = false;
      this.formData = this.getEmptyForm();
    },

    async createVersion() {
      if (!(await this.validateForm())) return;

      this.saving = true;
      try {
        const versionCode = String(this.formData.versionCode || '').trim().toUpperCase();
        const request = {
          versionCode,
          description: String(this.formData.description || '').trim(),
          effectiveStartDate: this.formData.effectiveStartDate || null,
          effectiveEndDate: this.formData.effectiveEndDate || null,
          isActive: this.formData.isActive === true,
        };
        const response = await this.callApi('CreateTaxConsentVersion', request);

        if (response.status === 0) {
          this.closeAddDialog();
          this.showSuccess('Consent version created successfully');
          this.$router.push('/TaxConsent/' + (response.data?.versionCode || versionCode));
        } else {
          this.showError(response.message || 'Failed to create consent version');
        }
      } catch (error) {
        console.error('Error creating consent version:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to create consent version'));
      } finally {
        this.saving = false;
      }
    },

    async duplicateActive() {
      this.duplicating = true;
      try {
        const response = await this.callApi('DuplicateTaxConsentActive', {});
        if (response.status === 0) {
          this.showSuccess('Active version duplicated successfully');
          this.$router.push('/TaxConsent/' + response.data?.versionCode);
        } else {
          this.showError(response.message || 'Failed to duplicate active version');
        }
      } catch (error) {
        console.error('Error duplicating active version:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to duplicate active version'));
      } finally {
        this.duplicating = false;
      }
    },

    async toggleActive(element, value) {
      // Single-active invariant: refuse to deactivate the only active version.
      // To switch the active version, the user turns ON another one (which
      // deactivates the rest). Leaving zero active would break the consent gate.
      if (!value && element.isActive) {
        element.isActive = true;
        this.showInfo('At least one version must stay active. Activate another version to switch.');
        return;
      }

      const previousValue = element.isActive;
      element.isActive = value;

      try {
        const response = await this.callApi('SetTaxConsentActive', element.consentVersionID, { isActive: value });
        if (response.status === 0) {
          // Reload so the single-active invariant reflects (turning one on turns others off).
          await this.loadVersions();
          this.showSuccess(`Version ${element.versionCode} is now active`);
        } else {
          element.isActive = previousValue;
          this.showError(response.message || 'Failed to update active status');
        }
      } catch (error) {
        element.isActive = previousValue;
        console.error('Error updating active status:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to update active status'));
      }
    },

    openDeleteDialog(element) {
      this.selectedVersion = element;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.selectedVersion) return;

      this.deleting = true;
      try {
        const response = await this.callApi('DeleteTaxConsentVersion', this.selectedVersion.consentVersionID);
        if (response.status === 0) {
          this.deleteDialog = false;
          this.selectedVersion = null;
          await this.loadVersions();
          this.showSuccess('Consent version deleted successfully');
        } else {
          this.showError(response.message || 'Failed to delete consent version');
        }
      } catch (error) {
        console.error('Error deleting consent version:', error);
        this.showError(this.extractErrorMessage(error, 'Failed to delete consent version'));
      } finally {
        this.deleting = false;
      }
    },

    showSuccess(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    showError(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },

    showInfo(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'info';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.no-data-container {
  padding: 48px 24px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  font-size: 1.1rem;
  color: #616161;
}

.no-data-subtitle {
  color: #9e9e9e;
  font-size: 0.875rem;
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 110px;
}

.version-cell {
  display: inline-flex;
  align-items: center;
}

.table-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-btn {
  transition: background-color 0.15s ease, color 0.15s ease;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.delete-btn:hover {
  color: rgb(var(--v-theme-error)) !important;
}

:deep(.my-data-table .v-table__wrapper) {
  max-height: 560px !important;
  overflow: auto !important;
}

:deep(.my-data-table table) {
  table-layout: fixed;
  min-width: 1210px;
  width: 100%;
}

:deep(.my-data-table thead th),
:deep(.my-data-table tbody td) {
  vertical-align: middle;
}

:deep(.my-data-table tbody td) {
  height: 56px;
  padding: 0 16px;
}

:deep(.my-data-table thead th:nth-child(1)),
:deep(.my-data-table tbody td:nth-child(1)) {
  width: 70px;
  min-width: 70px;
}

:deep(.my-data-table thead th:nth-child(2)),
:deep(.my-data-table tbody td:nth-child(2)) {
  width: 180px;
  min-width: 180px;
}

:deep(.my-data-table thead th:nth-child(3)),
:deep(.my-data-table tbody td:nth-child(3)) {
  width: 260px;
  min-width: 260px;
}

:deep(.my-data-table thead th:nth-child(4)),
:deep(.my-data-table tbody td:nth-child(4)),
:deep(.my-data-table thead th:nth-child(5)),
:deep(.my-data-table tbody td:nth-child(5)),
:deep(.my-data-table thead th:nth-child(6)),
:deep(.my-data-table tbody td:nth-child(6)) {
  width: 150px;
  min-width: 150px;
}

:deep(.my-data-table thead th:nth-child(7)),
:deep(.my-data-table tbody td:nth-child(7)) {
  width: 110px;
  min-width: 110px;
}

:deep(.my-data-table thead th:nth-child(8)),
:deep(.my-data-table tbody td:nth-child(8)) {
  width: 140px;
  min-width: 140px;
}
</style>
